import { FieldTypes } from "@certa/types";
import type { ReportCustomFilterCondition } from "@certa/types";
import { Select } from "@certa/catalyst/components/Select";
import { css } from "emotion";
import { isDateField } from "./ValueFieldInput/ValueFieldInput.utils";
import type { OperatorsDetails } from "../../../utils/filterUtils";
import {
  listTypeOperatorDetails,
  operatorsDetails,
  BADGES_OPERATOR_DETAILS
} from "../../../utils/filterUtils";

type OperatorFieldProps = {
  value: ReportCustomFilterCondition["operator"] | null;
  fieldType: FieldTypes;
  useRelativeDates: boolean;
  onChange: (value: string | null) => void;
  isORMReport?: boolean;
  customOperators?: OperatorsDetails;
  shouldUseNewLabelValueOptions: boolean;
  fieldPath?: string[];
};

export const OperatorField = ({
  onChange,
  value,
  fieldType,
  useRelativeDates,
  isORMReport,
  customOperators,
  shouldUseNewLabelValueOptions,
  fieldPath
}: OperatorFieldProps) => {
  const operators = getOperators({
    fieldType,
    useRelativeDates,
    isORMReport,
    customOperators,
    shouldUseNewLabelValueOptions,
    fieldPath
  });

  return (
    <div className={operatorWrapperStyle}>
      <Select
        menuPortalTarget={document.body}
        label="Operator"
        options={operators}
        value={value}
        width="100%"
        isSearchable={false}
        onChange={onChange}
        isClearable={false}
      />
    </div>
  );
};

/**
 * Have a min-width of 120px for the operator field
 * so that single words like 'contains' don't break to next line
 */
const operatorWrapperStyle = css({
  minWidth: "120px"
});

export const getOperators = ({
  fieldType,
  isORMReport,
  useRelativeDates,
  customOperators,
  shouldUseNewLabelValueOptions,
  fieldPath
}: Pick<
  OperatorFieldProps,
  | "fieldType"
  | "isORMReport"
  | "useRelativeDates"
  | "customOperators"
  | "shouldUseNewLabelValueOptions"
  | "fieldPath"
>) => {
  let newFieldType = fieldType;
  if (useRelativeDates && isDateField(newFieldType)) {
    newFieldType = FieldTypes.RELATIVE_DATE;
  }

  // Check if the field is badge_tags and return specific operators
  const lastPathElement = fieldPath?.[fieldPath.length - 1];
  if (lastPathElement?.includes("badge_tags")) {
    return BADGES_OPERATOR_DETAILS;
  }

  const operatorsToUse = customOperators || operatorsDetails;

  // TODO: This can be removed once we have a new label value options (DISPLAY_LABELS_FOR_SELECTABLE_FIELDS_IN_REPORTS)
  if (isORMReport && shouldUseNewLabelValueOptions) {
    return listTypeOperatorDetails;
  }

  const operators = operatorsToUse.filter(
    operator =>
      !operator.disAllowedFields.includes(newFieldType) &&
      !(isORMReport !== true && operator.isForORMOnly)
  );

  return operators;
};
